import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { CaptureFull } from "../lib/api-types";
import type { Vault } from "../contexts/vault-types";

type Mode = "manual" | "ai";

export default function Modify() {
  const { vault, slug } = useParams() as { vault: Vault; slug: string };
  const navigate = useNavigate();
  const [data, setData] = useState<CaptureFull | null>(null);
  const [mode, setMode] = useState<Mode>("manual");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [original, setOriginal] = useState("");
  const [instructions, setInstructions] = useState("");
  const [newProposal, setNewProposal] = useState<{ summary: string; tags: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/captures/${vault}/${slug}`)
      .then(async r => {
        if (cancelled) return;
        if (!r.ok) {
          setLoadError(r.status === 404 ? "Capture not found." : `Couldn't load (${r.status})`);
          return;
        }
        const d = (await r.json()) as CaptureFull;
        if (cancelled) return;
        setData(d); setTitle(d.title); setSummary(d.summary); setOriginal(d.originalText);
      })
      .catch(() => { if (!cancelled) setLoadError("Network error loading capture."); });
    return () => { cancelled = true; };
  }, [vault, slug]);

  if (loadError) return (
    <div className="p-8 text-center">
      <p className="text-red-600 text-sm mb-4">{loadError}</p>
      <button onClick={() => navigate(-1)} className="text-attic-accent text-sm">‹ Back</button>
    </div>
  );
  if (!data) return <div className="p-8 text-attic-dim">Loading…</div>;

  const saveManual = async () => {
    setError(null); setBusy(true);
    try {
      const r = await fetch(`/api/captures/${vault}/${slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, summary, originalText: original }),
      });
      if (!r.ok) { setError(`Save failed (${r.status})`); return; }
      navigate(-1);
    } catch {
      setError("Network error — changes not saved");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!instructions.trim()) return;
    setError(null); setBusy(true);
    try {
      const r = await fetch(`/api/captures/${vault}/${slug}/regenerate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instructions }),
      });
      if (!r.ok) {
        const detail = await r.text().catch(() => "");
        setError(`Regenerate failed (${r.status})${detail ? `: ${detail.slice(0, 120)}` : ""}`);
        return;
      }
      setNewProposal(await r.json());
    } catch {
      setError("Network error — try again");
    } finally {
      setBusy(false);
    }
  };

  const keep = async () => {
    if (!newProposal) return;
    setError(null); setBusy(true);
    try {
      const r = await fetch(`/api/captures/${vault}/${slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newProposal),
      });
      if (!r.ok) { setError(`Save failed (${r.status})`); return; }
      navigate(-1);
    } catch {
      setError("Network error — changes not saved");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 pt-3 pb-4">
      <button onClick={() => navigate(-1)} className="text-attic-accent text-sm mb-2">‹ Cancel</button>
      <h2 className="text-lg font-bold mb-3">Modify capture</h2>
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 text-xs rounded-lg px-3 py-2 mb-3 flex justify-between items-start">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 font-bold">×</button>
        </div>
      )}
      <div className="flex bg-attic-bg rounded-xl p-1 mb-4">
        <button
          onClick={() => setMode("manual")}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold ${mode === "manual" ? "bg-attic-surface text-attic-accent" : "text-attic-dim"}`}
        >
          Edit manually
        </button>
        <button
          onClick={() => setMode("ai")}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold ${mode === "ai" ? "bg-attic-surface text-attic-accent" : "text-attic-dim"}`}
        >
          Regenerate with AI
        </button>
      </div>

      {mode === "manual" ? (
        <>
          <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">Title</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full border border-attic-border rounded-lg px-2 py-2 text-xs mb-3"
          />
          <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">AI Summary</label>
          <textarea
            value={summary}
            onChange={e => setSummary(e.target.value)}
            className="w-full border border-attic-border rounded-lg px-2 py-2 text-xs mb-3 min-h-[90px]"
          />
          <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">Original</label>
          <textarea
            value={original}
            onChange={e => setOriginal(e.target.value)}
            className="w-full border border-attic-border rounded-lg px-2 py-2 text-xs mb-3 min-h-[120px] font-serif"
          />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => navigate(-1)} className="border border-attic-border rounded-lg py-2 text-xs">Cancel</button>
            <button onClick={saveManual} disabled={busy} className="bg-attic-accent text-white rounded-lg py-2 text-xs disabled:opacity-50">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">Current summary</label>
          <div className="border border-attic-border rounded-lg px-2 py-2 text-xs mb-3 bg-attic-bg text-attic-dim">{data.summary}</div>
          <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">What should be different?</label>
          <textarea
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            className="w-full border border-attic-border rounded-lg px-2 py-2 text-xs mb-3 min-h-[60px]"
            placeholder="e.g. focus on the actionable countermeasures"
          />
          <button onClick={regenerate} disabled={busy || !instructions.trim()} className="bg-attic-accent text-white rounded-lg py-2 text-xs w-full mb-3 disabled:opacity-50">
            {busy ? "Regenerating…" : "Regenerate summary & tags"}
          </button>
          {newProposal && (
            <>
              <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">New summary</label>
              <div className="bg-attic-accent-bg border-l-4 border-attic-accent rounded p-2 text-xs mb-3">{newProposal.summary}</div>
              <label className="text-[9px] uppercase font-bold text-attic-dim tracking-wider">New tags</label>
              <div className="flex flex-wrap gap-1 mb-3">
                {newProposal.tags.map(t => (
                  <span key={t} className="text-[10px] bg-attic-accent-bg text-attic-accent rounded-full px-2 py-0.5">{t}</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setNewProposal(null)} className="border border-attic-border rounded-lg py-2 text-xs">Discard</button>
                <button onClick={keep} disabled={busy} className="bg-attic-accent text-white rounded-lg py-2 text-xs disabled:opacity-50">
                  {busy ? "Saving…" : "Keep new version"}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
